import { useEffect, useState } from "react";
import api, { apiErrorMessage } from "@/api/client";
import { Loader2, Save, CheckCircle2, Lock, Eye, ArrowLeft, Shield, Paperclip } from "lucide-react";
import { DeadlinePill } from "@/components/DeadlinePill";
import "@/styles/activities_v3.scoped.css";

const TABS = ["Configuración", "Asistencia y Actividad", "Rúbrica"];
const DIM_OPTIONS = ["D1", "D2", "D3", "D4", "D5", "D6"];

// Preguntas fijas del registro práctico que ve el participante (idénticas a
// las de ActivityPage.js) — se muestran en la vista previa administrativa.
const QUESTION_LABELS = [
  { key: "situation", q: "1. ¿Qué situación abordaste?" },
  { key: "tool_used", q: "2. ¿Qué contenido, herramienta o idea del taller utilizaste?" },
  { key: "actions_taken", q: "3. ¿Qué hiciste concretamente?" },
  { key: "outcome", q: "4. ¿Qué resultado observaste?" },
  { key: "reflection", q: "5. ¿Qué aprendiste y qué harías diferente la próxima vez?" },
];

const ACTIVITY_STATUS_LABEL = {
  entregada: { label: "Entregada", cls: "bg-emerald-100 text-emerald-700" },
  pendiente: { label: "Pendiente", cls: "bg-amber-100 text-amber-700" },
  vencida: { label: "Vencida", cls: "bg-rose-100 text-rose-700" },
};

const ATTENDANCE_FILTER_KEYS = ["presente", "ausente"];

const FILTERS = [
  { key: "todos", label: "Todos" },
  { key: "entregada", label: "Entregada" },
  { key: "pendiente", label: "Pendiente" },
  { key: "vencida", label: "Vencida" },
  { key: "presente", label: "Presente" },
  { key: "ausente", label: "Ausente" },
];

export default function AdminActivities() {
  const [tab, setTab] = useState(0);
  const [configs, setConfigs] = useState(null);
  const [error, setError] = useState("");

  const loadConfigs = async () => {
    try {
      const { data } = await api.get("/admin/activity-configs");
      setConfigs(data.weeks);
    } catch (err) {
      setError(apiErrorMessage(err));
    }
  };

  useEffect(() => { loadConfigs(); }, []);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-black text-slate-800">Actividades Prácticas</h1>
        <p className="text-sm text-slate-500">5 talleres fijos. Las tareas se definen aquí antes de publicarse a los participantes.</p>
      </div>

      <div className="flex gap-2 border-b border-slate-200">
        {TABS.map((t, i) => (
          <button key={t} data-testid={`admin-activities-tab-${i}`} onClick={() => setTab(i)} className={`px-4 py-2.5 text-xs font-bold transition-colors ${tab === i ? "border-b-2 border-brand-accent text-brand-accent" : "text-slate-400 hover:text-slate-600"}`}>{t}</button>
        ))}
      </div>

      {error && <div className="text-rose-600 text-sm font-semibold">{error}</div>}
      {!configs ? <Loader2 className="w-6 h-6 animate-spin text-brand-accent" /> : (
        <>
          {tab === 0 && <ConfigTab configs={configs} reload={loadConfigs} />}
          {tab === 1 && <AttendanceActivityTab configs={configs} />}
          {tab === 2 && <RubricTab configs={configs} />}
        </>
      )}
    </div>
  );
}

function ConfigTab({ configs, reload }) {
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [previewWeek, setPreviewWeek] = useState(null);

  const openEdit = (cfg) => { setPreviewWeek(null); setEditing({ ...cfg, related_dimensions: cfg.related_dimensions || [] }); };
  const closePreview = () => setPreviewWeek(null);

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/admin/activity-configs/${editing.week}`, {
        title: editing.title, instructions: editing.instructions,
        open_date: editing.open_date, due_date: editing.due_date,
        published: editing.published, related_dimensions: editing.related_dimensions,
      });
      setEditing(null);
      reload();
    } finally {
      setSaving(false);
    }
  };

  const toggleDim = (d) => {
    setEditing((prev) => ({ ...prev, related_dimensions: prev.related_dimensions.includes(d) ? prev.related_dimensions.filter((x) => x !== d) : [...prev.related_dimensions, d] }));
  };

  return (
    <div className="space-y-4" data-testid="admin-activity-config-tab">
      {configs.map((cfg) => {
        const isPreviewing = previewWeek === cfg.week;
        const isEditing = editing?.week === cfg.week;
        const previewData = isEditing ? editing : cfg;
        return (
          <div key={cfg.week} className="bg-white rounded-2xl border border-slate-200 p-5 shadow-sm">
            <div className="flex items-center justify-between mb-3">
              <div>
                <span className="text-[10px] font-bold text-slate-400 uppercase">Semana {cfg.week} · Taller de {cfg.workshop_date}</span>
                <h3 className="text-sm font-black text-slate-800">{cfg.topic}</h3>
              </div>
              <div className="flex items-center gap-2">
                <span className={`text-[10px] font-bold uppercase px-2 py-1 rounded-md ${cfg.published ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>
                  {cfg.published ? "Publicada" : "No publicada"}
                </span>
                <DeadlinePill dueDate={cfg.due_date} published={cfg.published} />
              </div>
            </div>

            {isPreviewing ? (
              <ConfigPreview
                data={previewData}
                onEditConfig={() => openEdit(previewData)}
                onClose={closePreview}
              />
            ) : isEditing ? (
              <div className="space-y-3 border-t border-slate-100 pt-4">
                <div>
                  <label className="block text-[10px] font-bold text-slate-500 uppercase mb-1">Título de la tarea</label>
                  <input data-testid={`activity-config-title-${cfg.week}`} value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })} className="w-full text-sm border border-slate-300 rounded-lg px-3 py-2" />
                </div>
                <div>
                  <label className="block text-[10px] font-bold text-slate-500 uppercase mb-1">Indicaciones</label>
                  <textarea data-testid={`activity-config-instructions-${cfg.week}`} rows={3} value={editing.instructions} onChange={(e) => setEditing({ ...editing, instructions: e.target.value })} className="w-full text-sm border border-slate-300 rounded-lg px-3 py-2" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-[10px] font-bold text-slate-500 uppercase mb-1">Fecha apertura</label>
                    <input type="date" value={editing.open_date} onChange={(e) => setEditing({ ...editing, open_date: e.target.value })} className="w-full text-sm border border-slate-300 rounded-lg px-3 py-2" />
                  </div>
                  <div>
                    <label className="block text-[10px] font-bold text-slate-500 uppercase mb-1">Fecha entrega</label>
                    <input type="date" value={editing.due_date} onChange={(e) => setEditing({ ...editing, due_date: e.target.value })} className="w-full text-sm border border-slate-300 rounded-lg px-3 py-2" />
                  </div>
                </div>
                <div>
                  <label className="block text-[10px] font-bold text-slate-500 uppercase mb-1">Dimensiones relacionadas</label>
                  <div className="flex gap-2">
                    {DIM_OPTIONS.map((d) => (
                      <button key={d} type="button" onClick={() => toggleDim(d)} className={`px-2.5 py-1 rounded-lg text-[10px] font-bold border transition-colors ${editing.related_dimensions.includes(d) ? "bg-brand-slate text-white" : "bg-white text-slate-500 border-slate-300 hover:bg-slate-50"}`}>{d}</button>
                    ))}
                  </div>
                </div>
                <label className="flex items-center gap-2 text-xs font-semibold text-slate-600">
                  <input type="checkbox" checked={editing.published} onChange={(e) => setEditing({ ...editing, published: e.target.checked })} /> Publicar a participantes
                </label>
                <div className="flex gap-2 pt-2 flex-wrap">
                  <button data-testid={`activity-config-save-${cfg.week}`} disabled={saving} onClick={save} className="px-5 py-2 bg-brand-slate text-white text-xs font-bold rounded-xl flex items-center gap-2 transition-colors hover:bg-brand-slate/90 disabled:opacity-60">{saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />} Guardar</button>
                  <button data-testid={`activity-config-preview-${cfg.week}`} onClick={() => setPreviewWeek(cfg.week)} className="px-5 py-2 border border-brand-accent text-brand-accent text-xs font-bold rounded-xl flex items-center gap-2 transition-colors hover:bg-brand-accent/5"><Eye className="w-3.5 h-3.5" /> Vista previa participante</button>
                  <button onClick={() => setEditing(null)} className="px-5 py-2 border border-slate-300 text-xs font-bold rounded-xl text-slate-600 transition-colors hover:bg-slate-50">Cancelar</button>
                </div>
              </div>
            ) : (
              <div>
                <p className="text-xs text-slate-500 mb-3">{cfg.title || <span className="italic text-slate-400">Tarea aún sin definir</span>}</p>
                <div className="flex gap-4 flex-wrap">
                  <button data-testid={`activity-config-edit-${cfg.week}`} onClick={() => openEdit(cfg)} className="text-xs font-bold text-brand-accent hover:underline">Editar configuración →</button>
                  <button data-testid={`activity-config-preview-${cfg.week}`} onClick={() => setPreviewWeek(cfg.week)} className="text-xs font-bold text-slate-500 hover:text-slate-700 flex items-center gap-1.5"><Eye className="w-3.5 h-3.5" /> Vista previa participante</button>
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// Vista previa de solo lectura: reutiliza el mismo lenguaje visual del
// registro que verá el participante (activities_v3.scoped.css). No guarda
// respuestas ni genera ningún registro. Funciona aunque la actividad no
// esté publicada.
function ConfigPreview({ data, onEditConfig, onClose }) {
  return (
    <div className="anid-v3-scope" data-testid={`activity-preview-${data.week}`} style={{ marginTop: 12 }}>
      <div className="week-detail" style={{ marginBottom: 16 }}>
        <div className="side-heading">
          <p className="eyebrow">SEMANA {String(data.week).padStart(2, "0")} · VISTA PREVIA PARTICIPANTE</p>
          <h2>{data.title || <span style={{ color: "#98a0af", fontStyle: "italic" }}>Título de la tarea (aún sin definir)</span>}</h2>
          <div style={{ marginTop: 12, display: "flex", gap: 8, flexWrap: "wrap" }}>
            <span className="draft-pill">Apertura: {data.open_date || "—"}</span>
            <span className="draft-pill">Cierre: {data.due_date || "—"}</span>
          </div>
          <div className="side-rule" />
        </div>
        <div className="assignment">
          <div className="assignment-title"><span>Indicaciones</span></div>
          <p>{data.instructions || "Aún no se han definido indicaciones para esta actividad."}</p>
        </div>
      </div>

      <div className="workcard">
        <div className="card-top">
          <div><h2>Mi registro de la semana</h2><small>Así verá el participante las preguntas del registro práctico</small></div>
        </div>
        <div className="formbody">
          {QUESTION_LABELS.map((item) => (
            <div className="review-entry" key={item.key}>
              <small>{item.q}</small>
              <p style={{ color: "#a0aabb", fontStyle: "italic" }}>Respuesta libre del participante (texto).</p>
            </div>
          ))}
          <div className="tip"><Shield className="icon" /><p>El participante también puede declarar "No pude realizar la actividad" e informar una dificultad, en lugar de responder estas preguntas.</p></div>
        </div>
      </div>

      <div style={{ display: "flex", gap: 12, marginTop: 16, flexWrap: "wrap" }}>
        <button type="button" className="btn secondary" onClick={onClose} data-testid={`activity-preview-close-${data.week}`}><ArrowLeft className="icon" style={{ width: 15, height: 15 }} /> Cerrar vista previa</button>
        <button type="button" className="btn primary" onClick={onEditConfig} data-testid={`activity-preview-edit-${data.week}`}>Editar configuración</button>
      </div>
    </div>
  );
}

function AttendanceActivityTab({ configs }) {
  const [week, setWeek] = useState(configs[0]?.week || 1);
  const [rows, setRows] = useState(null);
  const [filter, setFilter] = useState("todos");
  const [error, setError] = useState("");
  const [viewingAnswers, setViewingAnswers] = useState(null);
  const [downloadingAttachment, setDownloadingAttachment] = useState(false);

  const load = async (w) => {
    setRows(null);
    setError("");
    try {
      const { data } = await api.get(`/admin/attendance-activity/${w}`);
      setRows(data.rows);
    } catch (err) {
      setError(apiErrorMessage(err));
    }
  };

  useEffect(() => { load(week); }, [week]);

  const setAttendance = async (participant_id, status) => {
    setRows((prev) => prev.map((r) => (r.participant_id === participant_id ? { ...r, attendance_status: status } : r)));
    try {
      await api.put("/admin/attendance", { participant_id, week, status });
    } catch (err) {
      setError(apiErrorMessage(err));
      load(week);
    }
  };

  const downloadAttachment = async (attachment) => {
    if (!attachment) return;
    setDownloadingAttachment(true);
    try {
      const res = await api.get(`/admin/activities/attachments/${attachment.attachment_id}/file`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url; a.download = attachment.original_filename || "adjunto"; document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError(apiErrorMessage(err, "No fue posible descargar el adjunto."));
    } finally {
      setDownloadingAttachment(false);
    }
  };

  const filtered = (rows || []).filter((r) => {
    if (filter === "todos") return true;
    if (ATTENDANCE_FILTER_KEYS.includes(filter)) return r.attendance_status === filter;
    return r.activity_status === filter;
  });

  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm" data-testid="admin-attendance-activity-tab">
      <div className="mb-4">
        <p className="text-xs text-slate-500 mb-3">La <b>asistencia</b> se registra manualmente y es independiente del estado de la <b>actividad</b>, que se actualiza automáticamente según el envío del participante.</p>
        <div className="flex gap-2 flex-wrap">
          {configs.map((c) => <button key={c.week} data-testid={`attendance-week-btn-${c.week}`} onClick={() => setWeek(c.week)} className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-colors ${week === c.week ? "bg-brand-slate text-white" : "bg-slate-100 text-slate-500 hover:bg-slate-200"}`}>Semana {c.week}</button>)}
        </div>
      </div>

      <div className="flex gap-2 mb-5 flex-wrap">
        {FILTERS.map((f) => (
          <button key={f.key} data-testid={`attendance-filter-${f.key}`} onClick={() => setFilter(f.key)} className={`px-3 py-1 rounded-full text-[11px] font-bold border transition-colors ${filter === f.key ? "bg-brand-accent text-white border-brand-accent" : "bg-white text-slate-500 border-slate-200 hover:bg-slate-50"}`}>{f.label}</button>
        ))}
      </div>

      {error && <div className="text-rose-600 text-sm font-semibold mb-3">{error}</div>}

      {!rows ? <Loader2 className="w-5 h-5 animate-spin text-brand-accent" /> : (
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="text-left text-slate-400 uppercase text-[10px] border-b">
                <th className="py-2 pr-2">Participante</th>
                <th className="pr-2">Unidad</th>
                <th className="pr-2">Asistencia</th>
                <th className="pr-2">Actividad</th>
                <th>Fecha de entrega</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((r) => {
                const act = ACTIVITY_STATUS_LABEL[r.activity_status];
                return (
                  <tr key={r.participant_id} className="border-b border-slate-100" data-testid={`attendance-row-${r.participant_id}`}>
                    <td className="py-2.5 pr-2 font-semibold text-slate-700">{r.full_name}</td>
                    <td className="pr-2 text-slate-500">{r.unit}</td>
                    <td className="pr-2">
                      <select
                        value={r.attendance_status}
                        onChange={(e) => setAttendance(r.participant_id, e.target.value)}
                        data-testid={`attendance-select-${r.participant_id}`}
                        className="text-xs border border-slate-300 rounded-lg px-2 py-1 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-accent"
                      >
                        <option value="no_registrada">No registrada</option>
                        <option value="presente">Presente</option>
                        <option value="ausente">Ausente</option>
                      </select>
                    </td>
                    <td className="pr-2">
                      <span data-testid={`activity-status-${r.participant_id}`} className={`text-[10px] font-bold uppercase px-2 py-1 rounded-md ${act.cls}`}>{act.label}</span>
                    </td>
                    <td className="text-slate-500">
                      {r.submitted_at_display || "—"}
                      {r.activity_status === "entregada" && (
                        <button
                          type="button"
                          onClick={() => setViewingAnswers(r)}
                          data-testid={`view-answers-${r.participant_id}`}
                          className="ml-2 text-[10px] font-bold text-brand-accent hover:underline transition-colors"
                        >
                          Ver respuestas
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {filtered.length === 0 && <p className="text-center text-xs text-slate-400 py-8">No hay participantes que coincidan con el filtro seleccionado.</p>}
        </div>
      )}

      {viewingAnswers && (
        <div className="fixed inset-0 z-50 bg-[color:var(--brand-navy)]/60 flex items-center justify-center p-4" onClick={() => setViewingAnswers(null)}>
          <div className="bg-white rounded-2xl max-w-xl w-full max-h-[85vh] overflow-y-auto p-6" data-testid="view-answers-modal" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-sm font-black text-slate-800">{viewingAnswers.full_name}</h2>
                <p className="text-[10px] text-slate-400">Semana {week} · Respuestas registradas (solo lectura) · Entregado {viewingAnswers.submitted_at_display || "—"}</p>
              </div>
              <button type="button" onClick={() => setViewingAnswers(null)} data-testid="view-answers-close-btn" className="p-1 rounded-full hover:bg-slate-100 transition-colors text-slate-500 text-xs font-bold">Cerrar</button>
            </div>
            <div className="space-y-3 text-xs">
              {QUESTION_LABELS.map((item) => (
                <div key={item.key}>
                  <p className="font-bold text-slate-500 mb-0.5">{item.q}</p>
                  <p className="text-slate-700">{viewingAnswers.fields?.[item.key] || <span className="text-slate-400 italic">Sin respuesta registrada.</span>}</p>
                </div>
              ))}
              <div className="pt-2 border-t border-slate-100">
                <p className="font-bold text-slate-500 mb-1.5">Evidencia adjunta</p>
                {viewingAnswers.attachment ? (
                  <button
                    type="button"
                    onClick={() => downloadAttachment(viewingAnswers.attachment)}
                    disabled={downloadingAttachment}
                    data-testid="view-answers-attachment-download-btn"
                    className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-brand-accent text-brand-accent text-[11px] font-bold transition-colors hover:bg-brand-accent/5 disabled:opacity-60"
                  >
                    {downloadingAttachment ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Paperclip className="w-3.5 h-3.5" />}
                    {viewingAnswers.attachment.original_filename}
                  </button>
                ) : (
                  <span className="text-slate-400 italic">Sin adjunto (opcional).</span>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function RubricTab({ configs }) {
  const [week, setWeek] = useState(configs[0]?.week || 1);
  const [rubric, setRubric] = useState(null);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState("");

  const load = async (w) => {
    const { data } = await api.get(`/admin/rubrics/${w}`);
    setRubric(data);
  };

  useEffect(() => { load(week); setMsg(""); }, [week]);

  const updateCriterion = (idx, key, value) => {
    setRubric((prev) => {
      const criteria = [...prev.criteria];
      criteria[idx] = { ...criteria[idx], [key]: value };
      return { ...prev, criteria };
    });
  };
  const updateDescriptor = (idx, level, value) => {
    setRubric((prev) => {
      const criteria = [...prev.criteria];
      criteria[idx] = { ...criteria[idx], descriptors: { ...criteria[idx].descriptors, [level]: value } };
      return { ...prev, criteria };
    });
  };

  const save = async () => {
    setSaving(true); setMsg("");
    try {
      const { data } = await api.put(`/admin/rubrics/${week}`, { criteria: rubric.criteria });
      setRubric(data);
      setMsg("Guardado como borrador.");
    } finally {
      setSaving(false);
    }
  };

  const approve = async () => {
    try {
      const { data } = await api.post(`/admin/rubrics/${week}/approve`);
      setRubric(data);
      setMsg("Rúbrica aprobada. Ya puede usarse para revisar entregas.");
    } catch (err) {
      setMsg(apiErrorMessage(err));
    }
  };

  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm" data-testid="admin-rubric-tab">
      <div className="flex gap-2 mb-4 flex-wrap">
        {configs.map((c) => <button key={c.week} onClick={() => setWeek(c.week)} className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-colors ${week === c.week ? "bg-brand-slate text-white" : "bg-slate-100 text-slate-500 hover:bg-slate-200"}`}>Semana {c.week}</button>)}
      </div>
      {!rubric ? <Loader2 className="w-5 h-5 animate-spin text-brand-accent" /> : (
        <div className="space-y-4">
          <div className="flex items-center gap-3 flex-wrap">
            <span data-testid="rubric-status-badge" className={`text-[10px] font-bold uppercase px-2 py-1 rounded-md ${rubric.status === "approved" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>{rubric.status === "approved" ? "Aprobada" : "Borrador"} · v{rubric.version}</span>
            {rubric.status === "approved" ? (
              <span className="text-[10px] text-slate-400 flex items-center gap-1" data-testid="rubric-approved-at"><CheckCircle2 className="w-3 h-3 text-emerald-600" /> Aprobada el {rubric.approved_at_display || "—"} · Editar creará una nueva versión</span>
            ) : (
              <span className="text-[10px] text-slate-400 flex items-center gap-1"><Lock className="w-3 h-3" /> Borrador: no permite evaluar entregas ni asignar IAC</span>
            )}
          </div>
          {rubric.criteria.map((c, idx) => (
            <div key={c.id} className="border border-slate-200 rounded-xl p-4">
              <input data-testid={`rubric-criterion-name-${week}-${idx}`} value={c.name} onChange={(e) => updateCriterion(idx, "name", e.target.value)} className="w-full font-bold text-sm border-b border-slate-200 pb-1 mb-3 focus:outline-none" />
              <div className="grid grid-cols-1 sm:grid-cols-5 gap-2">
                {["0","1","2","3","4"].map((lvl) => (
                  <div key={lvl}>
                    <label className="text-[9px] font-bold text-slate-400 uppercase">Nivel {lvl}</label>
                    <textarea rows={2} value={c.descriptors[lvl] || ""} onChange={(e) => updateDescriptor(idx, lvl, e.target.value)} className="w-full text-[11px] border border-slate-200 rounded-lg p-1.5" />
                  </div>
                ))}
              </div>
            </div>
          ))}
          {msg && <p className="text-xs font-semibold text-emerald-600">{msg}</p>}
          <div className="flex gap-3 flex-wrap">
            <button data-testid="rubric-save-btn" disabled={saving} onClick={save} className="px-5 py-2.5 bg-brand-slate text-white text-xs font-bold rounded-xl flex items-center gap-2 transition-colors hover:bg-brand-slate/90 disabled:opacity-60">{saving && <Loader2 className="w-3.5 h-3.5 animate-spin" />} Guardar borrador</button>
            <button data-testid="rubric-approve-btn" disabled={rubric.status === "approved"} onClick={approve} className="px-5 py-2.5 bg-emerald-600 text-white text-xs font-bold rounded-xl flex items-center gap-2 transition-colors hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed"><CheckCircle2 className="w-3.5 h-3.5" /> Aprobar rúbrica</button>
          </div>
        </div>
      )}
    </div>
  );
}
